import { Head } from "@inertiajs/react";
import MarketingLayout from "@/layouts/MarketingLayout";
import SiteNav from "@/shared/marketing/SiteNav";
import PageHero from "@/shared/marketing/PageHero";
import { type ReactNode } from "react";

const BC = { fontFamily: "'Oswald', sans-serif" } as const;

// ── Tile ──────────────────────────────────────────────────────────────────────
interface TileProps {
  category: string;
  title: string;
  desc: string;
  imagePath: string;
  fallbackColor: string;
  href: string;
  className?: string;
  titleSize?: string;
}

function Tile({ category, title, desc, imagePath, fallbackColor, href, className = "", titleSize = "clamp(2.4rem, 3.5vw, 3.2rem)" }: TileProps) {
  return (
    <a
      href={href}
      className={`group relative overflow-hidden block no-underline ${className}`}
      style={{ background: fallbackColor }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        style={{ backgroundImage: `url(${imagePath})` }}
      />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)" }}
      />
      <div
        className="absolute inset-0 bg-[#ff2e89] opacity-0 group-hover:opacity-[0.055] transition-opacity duration-300 pointer-events-none"
        style={{ mixBlendMode: "color" }}
      />
      <div className="absolute top-5 right-5 w-9 h-9 border border-white/20 flex items-center justify-center group-hover:border-[#ff2e89] group-hover:bg-[#ff2e89] transition-colors duration-200 text-white group-hover:text-[#0b0b0b]">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 8h10M8 3l5 5-5 5" />
        </svg>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
        <p className="text-[#ff2e89] text-[0.72rem] font-bold tracking-[0.28em] uppercase mb-1.5" style={BC}>
          {category}
        </p>
        <h2 className="text-white font-black uppercase leading-none mb-3" style={{ ...BC, fontSize: titleSize }}>
          {title}
        </h2>
        <div
          className="border-l-2 border-[#ff2e89] pl-3.5 py-2 opacity-100 translate-y-0 md:opacity-0 md:translate-y-1.5 group-hover:opacity-100! group-hover:translate-y-0! transition-all duration-300 max-w-85"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
        >
          <p className="text-[rgba(240,236,232,0.82)] text-sm leading-relaxed">{desc}</p>
        </div>
      </div>
    </a>
  );
}

// ── Section divider ────────────────────────────────────────────────────────────
function SectionHead({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4 px-6 md:px-10 pt-14 pb-6 max-w-360 mx-auto">
      <span className="text-[#ff2e89] text-[0.78rem] font-bold tracking-[0.32em] uppercase whitespace-nowrap" style={BC}>
        {label}
      </span>
      <div className="flex-1 h-px bg-[#242424]" />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
interface Props {
  images: {
    tracks: string;
    events: string;
    clubs: string;
    guides: string;
  };
}

export default function Home({ images }: Props) {
  return (
    <>
      <Head title="Driftables — The Drift Community Hub" />

      <SiteNav />

      <PageHero
        eyebrow="The Drift Community Hub"
        titleSolid="Find Your"
        titleOutline="Track."
        subtitle="Discover drift tracks, events, and clubs across North America. The central platform for drivers, media, teams, and organizers to connect — before, during, and after every event."
        primaryCta={{ label: "Explore Now", href: "#explore" }}
        secondaryCta={{ label: "For Organizers", href: "/organizers" }}
        ghostText="DRIFT"
        titleSize="clamp(5rem, 13vw, 11rem)"
      />

      {/* ── EXPLORE ── */}
      <div id="explore">
        <SectionHead label="Explore" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 grid-rows-[200px_200px_200px_200px] md:grid-rows-[480px_320px] gap-0.75 px-6 md:px-10 max-w-360 mx-auto">
        <Tile
          className="col-span-1 md:col-span-7 row-span-1 md:row-span-2"
          category="Directory" title="Tracks"
          titleSize="clamp(3rem, 5vw, 5rem)"
          desc="Find drift tracks near you. Layouts, surface info, contact details, and community notes — all in one place."
          imagePath={images.tracks} fallbackColor="#0e1a0e" href="/tracks"
        />
        <Tile
          className="col-span-1 md:col-span-5"
          category="Calendar" title="Events"
          desc="Practice days, competitions, and invitationals. Apply as a driver, media, team, or crew."
          imagePath={images.events} fallbackColor="#12101c" href="/events"
        />
        <Tile
          className="col-span-1 md:col-span-5"
          category="Community" title="Clubs"
          desc="Find your local drift crew or connect with regional clubs."
          imagePath={images.clubs} fallbackColor="#1a1008" href="/clubs"
        />
        {/* Guides tile re-enable when ready:
        <Tile
          className="col-span-1 md:col-span-2"
          category="Learn" title="Guides"
          desc="Build specs, technique, and event prep."
          imagePath={images.guides} fallbackColor="#0e1218" href="/guides"
        /> */}
      </div>

      {/* ── FOR ORGANIZERS ── */}
      <div id="organizers">
        <SectionHead label="For Organizers" />
      </div>

      <div className="px-6 md:px-10 max-w-360 mx-auto mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 border border-[#242424] relative overflow-hidden" style={{ background: "#141414" }}>
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#ff2e89]" />

          <div className="p-8 md:p-12">
            <p className="text-[#ff2e89] text-[0.75rem] font-bold tracking-[0.3em] uppercase mb-3" style={BC}>
              Track Managers &amp; Event Hosts
            </p>
            <h2 className="text-[#f0ece8] font-black uppercase leading-none mb-4" style={{ ...BC, fontSize: "clamp(2rem, 3.5vw, 3rem)" }}>
              Run Your Event.<br />Your Way.
            </h2>
            <p className="text-[#888] text-base leading-relaxed mb-8 max-w-[46ch]">
              Driftables gives organizers powerful tools to manage events end-to-end — from applications and invitations to credentialing and participant history.
            </p>

            <ul className="list-none p-0 m-0 flex flex-col gap-3 mb-10">
              {[
                "Driver, media, staff, and crew applications in one dashboard",
                "Send invitations directly to specific participants",
                "Full event history — see who was at every past event",
                "Role and staff assignment management",
                "Public-facing event pages for your community",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-[rgba(240,236,232,0.65)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ff2e89] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <a
              href="/organizers"
              className="no-underline inline-block uppercase font-bold tracking-widest text-sm bg-[#ff2e89] text-[#0b0b0b] px-7 py-3 hover:opacity-80 transition-opacity"
              style={BC}
            >
              Get Started as an Organizer
            </a>
          </div>

          <div
            className="hidden md:flex items-center justify-center relative overflow-hidden min-h-75"
            style={{ background: "linear-gradient(145deg, #130a10 0%, #0b0b0b 100%)" }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(255,46,137,0.08) 0%, transparent 65%)" }} />
            <span
              className="text-transparent select-none leading-tight text-center relative z-10"
              style={{ ...BC, fontSize: "clamp(5rem, 10vw, 9rem)", fontWeight: 700, WebkitTextStroke: "1px rgba(255,46,137,0.2)" }}
            >
              EVENT<br />MGMT
            </span>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="border-t border-[#242424] px-6 md:px-10 py-7 flex flex-col md:flex-row items-center justify-between gap-2">
        <span className="text-[#555] text-sm font-black tracking-widest uppercase" style={BC}>
          Drift<span className="text-[#ff2e89]" style={BC}>ables</span>
        </span>
        <span className="text-[#555] text-xs">&copy; 2026 Driftables. All rights reserved.</span>
      </footer>
    </>
  );
}

Home.layout = (page: ReactNode) => <MarketingLayout>{page}</MarketingLayout>;
